import React, { Component } from 'react';
import { Form, actions } from 'react-redux-form';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { fetchSuitabilityInfo, updateSuitabilityInfo } from '../../actions/suitability';
import { fetchUser } from '../../actions';
import './styles.css';
import Tooltip from '../Tooltip';
import { LAYOUT } from '../Layout/enums';
import SuitabilityInputRow from '../Forms/Suitability/SuitabilityInputRow';
import loadGif from '../../assets/images/loading.gif';

import {
  baseSuitabilityPath,
  taxRateValues,
  expensesValues,
  specialExpensesValues,
  timeFrameForSpecialExpensesValues,
  liquidityNeedsValues,
  liquidityNeedsSubTexts,
  riskToleranceValues,
  riskToleranceSubTexts,
  investmentObjectivesValues,
  investmentObjectivesSubTexts,
  financialInvestmentExperienceLabels,
  yearsOfExperience,
  transactionsPerYear,
  decisionMakingOptions,
  investmentOptions,
  investmentTimespanOptions,
  investmentTimespanSubTexts,
  fundingOptionLabels,
  PLACEHOLDER_OPT,
} from '../Forms/Schemas/baseRrf';

const options = {
  disabled: true,
};

// Tooltips
const investmentExperienceTooltip = 'Asset types you are currently trading or have traded. This may include investments and transactions performed by you or on your behalf.';

const financialSituationFields = ({ suitabilityInfo }) => ([{
  model: '.financialSituation.taxRate',
  defaultValue: suitabilityInfo.financialSituation.taxRate || '-',
  display: taxRateValues[suitabilityInfo.financialSituation.taxRate],
  label: 'Tax Rate',
  inputType: 'select',
  options: taxRateValues,
  errors: {
    required: val => !val || !val.length || val === PLACEHOLDER_OPT || val === '-',
  },
  errorHandler: {
    messages: {
      required: 'Please select a tax rate range.',
    },
  },
},
{
  model: '.financialSituation.annualExpenses',
  defaultValue: suitabilityInfo.financialSituation.annualExpenses || '-',
  display: expensesValues[suitabilityInfo.financialSituation.annualExpenses],
  label: 'Annual Expenses',
  inputType: 'select',
  options: expensesValues,
  tooltip: 'Recurring: Annual expenses might include mortgage payments, rent, long-term debts, utilities, child support, alimony, etc.',
  errors: {
    required: val => !val || !val.length || val === PLACEHOLDER_OPT || val === '-',
  },
  errorHandler: {
    messages: {
      required: 'Please select your annual expenses range.',
    },
  },
},
{
  model: '.financialSituation.specialExpenses',
  defaultValue: suitabilityInfo.financialSituation.specialExpenses || '-',
  display: specialExpensesValues[suitabilityInfo.financialSituation.specialExpenses],
  label: 'Special Expenses',
  inputType: 'select',
  options: specialExpensesValues,
  tooltip: 'Non-Recurring: Special expenses might include a home purchase, remodeling a home, a car purchase, education, medical expenses, etc.',
  errors: {
    required: val => !val || !val.length || val === PLACEHOLDER_OPT || val === '-',
  },
  errorHandler: {
    messages: {
      required: 'Please select your special expenses range.',
    },
  },
},
{
  model: '.financialSituation.timeFrameSpecialExpenses',
  defaultValue: suitabilityInfo.financialSituation.timeFrame || '-',
  display: timeFrameForSpecialExpensesValues[suitabilityInfo.financialSituation.timeFrame],
  label: 'Time frame for special expenses',
  inputType: 'select',
  options: timeFrameForSpecialExpensesValues,
  errors: {
    required: val => !val || !val.length || val === PLACEHOLDER_OPT || val === '-',
  },
  errorHandler: {
    messages: {
      required: 'Please select a time frame for special expenses.',
    },
  },
},
{
  model: '.financialSituation.liquidityNeeds',
  defaultValue: suitabilityInfo.financialSituation.liquidityNeeds || '-',
  display: liquidityNeedsValues[suitabilityInfo.financialSituation.liquidityNeeds],
  label: 'Liquidity Needs',
  inputType: 'radio',
  options: liquidityNeedsValues,
  subTextOptions: liquidityNeedsSubTexts,
  tooltip: 'Liquidity is the ability to quickly and easily convert to cash all, or a portion, of the investments in this account without experiencing significant loss in value from, for example, the lack of a ready market, or incurring significant costs or penalties.',
  errors: {
    required: val => !val || !val.length || val === PLACEHOLDER_OPT || val === '-',
    isValidOpt: val => !(val === 'somewhat_important' || val === 'does_not_matter'),
  },
  errorHandler: {
    messages: {
      required: 'Please select an option.',
      isValidOpt: 'The denominator is invalid',
    },
  },
  disclaimer: 'In order to qualify for My IPO Offers your Liquidity Needs must be “Somewhat Important” or “Not Important"',
}]);

const investmentRiskToleranceFields = ({ suitabilityInfo }) => ([{
  model: '.investmentRiskTolerance.riskTolerance',
  defaultValue: suitabilityInfo.investmentRiskTolerance.riskTolerance || '-',
  display: riskToleranceValues[suitabilityInfo.investmentRiskTolerance.riskTolerance],
  label: 'Risk Tolerance',
  inputType: 'radio',
  options: riskToleranceValues,
  subTextOptions: riskToleranceSubTexts,
  errors: {
    required: val => !val || !val.length || val === PLACEHOLDER_OPT || val === '-',
    isValidOpt: val => !(val === 'moderately_aggressive' || val === 'significant_risk'),
  },
  errorHandler: {
    messages: {
      required: 'Please select an option.',
      isValidOpt: 'The denominator is invalid',
    },
  },
  disclaimer: 'In order to qualify for My IPO Offers your Investment Risk Tolerance must be “Moderately Aggressive” or “Significant Risk”',
},
{
  model: '.investmentRiskTolerance.investmentObjectives',
  defaultValue: suitabilityInfo.investmentRiskTolerance.investmentObjectives || '-',
  display: investmentObjectivesValues[suitabilityInfo.investmentRiskTolerance.investmentObjectives],
  label: 'Investment Objectives',
  inputType: 'radio',
  options: investmentObjectivesValues,
  subTextOptions: investmentObjectivesSubTexts,
  errors: {
    required: val => !val || !val.length || val === PLACEHOLDER_OPT || val === '-',
    isValidOpt: val => !(val === 'maximum_growth' || val === 'speculation'),
  },
  errorHandler: {
    messages: {
      required: 'Please select an option.',
      isValidOpt: 'The denominator is invalid',
    },
  },
  disclaimer: 'In order to qualify for My IPO Offers your Investment Objectives must be “Maximum Growth” or “Speculation”',
},
]);

const financialInvestmentExperience = ({ suitabilityInfo, changeFieldValue, changeValidity }) => ([{
  model: '.investmentExperience',
  inputType: 'customInvestmentExperience',
  options: suitabilityInfo.investmentExperience,
  optionLabels: financialInvestmentExperienceLabels,
  changeFieldValue,
  changeValidity,
  yearsOfExperience,
  transactionsPerYear,
  decisionMakingOptions,
  PLACEHOLDER_OPT,
},
]);

const investmentObjectives = ({ suitabilityInfo, changeFieldValue, changeValidity }) => ([
  {
    model: '.investmentObjectives.investments',
    defaultValue: suitabilityInfo.investmentObjectives.investments || '-',
    display: investmentOptions[suitabilityInfo.investmentObjectives.investments],
    label: 'The investments in this account will be',
    inputType: 'radio',
    options: investmentOptions,
    subTextOptions: null,
    errors: {
      required: val => !val || !val.length || val === PLACEHOLDER_OPT || val === '-',
    },
    errorHandler: {
      messages: {
        required: 'Please select an investment objective.',
      },
    },
  },
  {
    model: '.investmentObjectives.investmentTimespan',
    defaultValue: suitabilityInfo.investmentObjectives.investmentTimespan || '-',
    display: investmentTimespanOptions[suitabilityInfo.investmentObjectives.investmentTimespan],
    label: 'Time Horizon',
    inputType: 'radio',
    options: investmentTimespanOptions,
    subTextOptions: investmentTimespanSubTexts,
    errors: {
      required: val => !val || !val.length || val === PLACEHOLDER_OPT || val === '-',
      isValidOpt: val => !(val === 'average' || val === 'longest'),
    },
    errorHandler: {
      messages: {
        required: 'Please select an option.',
        isValidOpt: 'The denominator is invalid',
      },
    },
    disclaimer: 'In order to qualify for My IPO Offers your Time Horizon must be “Average” or "Longest"',
  },
  {
    model: '.investmentObjectives.fundingOption',
    defaultValue: '-',
    label: 'How you are funding this account',
    inputType: 'customInvestmentObjective',
    options: suitabilityInfo.investmentObjectives.fundingOption,
    optionLabels: fundingOptionLabels,
    changeFieldValue,
    changeValidity,
  },
]);

const allowConditionalEdit = ({ disabled, submitting, partialEditIdentifier }, inputMsg, toggleForm, currentPartialEdit) =>
  ((disabled || partialEditIdentifier !== currentPartialEdit) ? (
    <div
      styleName="edit marginTop15"
      style={{ 'pointer-events': submitting || !disabled ? 'none' : '' }}
      onClick={() => toggleForm(undefined, currentPartialEdit)}
      onKeyPress={() => toggleForm(undefined, currentPartialEdit)}
      role="button"
      tabIndex={0}
    >
  Edit
    </div>
  ) :
    (
      <div styleName="saveOrCancel marginTop15">
        <input
          id="btnEditAndSave"
          type="submit"
          value={inputMsg}
          disabled={submitting}
          styleName="formHeadlink"
        />
        { !submitting &&
        <div
          styleName="formHeadlink"
          onClick={() => toggleForm('cancel', currentPartialEdit)}
          onKeyPress={() => toggleForm('cancel', currentPartialEdit)}
          role="link"
          tabIndex={0}
        >
        Cancel
        </div>
      }
      </div>
    ));

class Suitability extends Component {
  constructor(props) {
    super(props);
    this.state = {
      submitting: false,
      disabled: true,
      ready: true,
      mounted: false,
      partialEditIdentifier: '',
    };
  }

  async componentWillMount() {
    await this.props.fetchSuitabilityInfo();
    this.setState({
      mounted: true,
    });
  }

  onSubmit = async () => {
    const { submitting } = this.state;
    if (submitting || !this.props.formData) {
      return;
    }
    this.setState({ submitting: true });
    const suitabilityInformation = this.props.formData;
    await this.props.updateSuitabilityInfo(suitabilityInformation, true, this.state.partialEditIdentifier);
    await this.props.fetchUser({ cacheDuration: 0 });
    await this.props.fetchSuitabilityInfo({ cacheDuration: 0 });

    this.setState({
      disabled: true,
      submitting: false,
    });
  };

  toggleForm = () => {
    const updatedState = { disabled: !this.state.disabled };
    this.setState(updatedState);
  };

  partialToggleForm = (type, identifier) => {
    let idntf = '';
    if (type !== 'cancel') {
      idntf = identifier;
    }
    const updatedState = { disabled: !this.state.disabled, partialEditIdentifier: idntf };
    this.setState(updatedState);
  };

  render() {
    if (!this.state.ready) {
      return null;
    }

    const {
      disabled, submitting, mounted, partialEditIdentifier,
    } = this.state;
    options.disabled = disabled;
    options.auto = 'labels';
    let inputMsg = 'Save';
    if (submitting) {
      inputMsg = 'Saving...';
    } else if (disabled) {
      inputMsg = 'Edit';
    }
    let styleSuff = '';
    switch (this.props.layout) {
      case LAYOUT.TAB:
        styleSuff = 'Tab';
        break;
      case LAYOUT.MOB:
        styleSuff = 'Mob';
        break;
        // no default
    }
    if (mounted) {
      return (
        <div styleName={`formGroup${styleSuff}`}>
          <Form
            model={`${baseSuitabilityPath}.suitabilityInformation`}
            onSubmit={this.onSubmit}
          >
            <div styleName="suitabilityFormSection">
              <div styleName="suitabilityInfoHeader">
                <div styleName={`labelBlack${styleSuff} title`}>
                  Suitability Information
                </div>
              </div>
              <div styleName="suitabilityInfoHeader">
                <div styleName={`labelBlack${styleSuff} subTitle`}>
                  Financial Situation and Needs, Liquidity Considerations, and Tax Status
                </div>
                {
                  allowConditionalEdit(this.state, inputMsg, this.partialToggleForm, 'financialSituation')
                }
              </div>
              { financialSituationFields(this.props).map(f => (
                <SuitabilityInputRow
                  key={f.model}
                  editMode={disabled ? 'static' : 'editting'}
                  isPartialEdit={partialEditIdentifier === 'financialSituation'}
                  label={f.label}
                  {...f}
                />))
              }
              <div styleName="suitabilityInfoHeader extraMargin">
                <div styleName={`labelBlack${styleSuff} subTitle`}>
                  Investment Risk Tolerance
                </div>
                {
                  allowConditionalEdit(this.state, inputMsg, this.partialToggleForm, 'investmentRiskTolerance')
                }
              </div>
              { investmentRiskToleranceFields(this.props).map(f => (
                <SuitabilityInputRow
                  key={f.model}
                  editMode={disabled ? 'static' : 'editting'}
                  label={f.label}
                  isPartialEdit={partialEditIdentifier === 'investmentRiskTolerance'}
                  {...f}
                />))
              }
              <div styleName="suitabilityInfoHeader">
                <div styleName={`labelBlack${styleSuff} subTitle extraMargin`}>
                  Investment Experience
                  { !disabled ? (<span styleName="checkAllInSubTitle">(check all that apply)</span>) : ('')}
                  <Tooltip
                    styles={{ display: 'inline-block', marginLeft: '3px' }}
                    text={investmentExperienceTooltip}
                  />
                </div>
                {
                  allowConditionalEdit(this.state, inputMsg, this.partialToggleForm, 'financialInvestment')
                }
              </div>
              { financialInvestmentExperience(this.props).map(f => (
                <SuitabilityInputRow
                  key={f.model}
                  editMode={disabled ? 'static' : 'editting'}
                  label={f.label}
                  isPartialEdit={partialEditIdentifier === 'financialInvestment'}
                  {...f}
                />))
              }
              <div styleName="suitabilityInfoHeader">
                <div styleName={`labelBlack${styleSuff} subTitle extraMargin`}>
                  Investment Objectives and Investment Time Horizon
                </div>
                {
                  allowConditionalEdit(this.state, inputMsg, this.partialToggleForm, 'investmentObjectives')
                }
              </div>
              { investmentObjectives(this.props).map(f => (
                <SuitabilityInputRow
                  key={f.model}
                  editMode={disabled ? 'static' : 'editting'}
                  label={f.label}
                  isPartialEdit={partialEditIdentifier === 'investmentObjectives'}
                  {...f}
                />))
              }
            </div>
          </Form>
        </div>
      );
    }
    return (
      <div styleName="loading">
        <img
          src={loadGif}
          alt="Loading"
        />
      </div>
    );
  }
}

Suitability.propTypes = {
  layout: PropTypes.number,
};

Suitability.defaultProps = {
  layout: LAYOUT.DESK,
};

const mapStateToProps = state => ({
  suitabilityInfo: state.suitabilityInformation.data,
  suitabilityInfoIsFetching: get(state, 'suitabilityInformation.isFetching'),
  authToken: state.authToken,
  formData: get(state, 'suitabilityForm.suitabilityInformation'),
  solicitedStatus: get(state, 'user.data.solicitedStatus'),
});

const mapDispatchToProps = dispatch => ({
  fetchSuitabilityInfo: (...args) => (dispatch(fetchSuitabilityInfo(...args))),
  fetchUser: (...args) => (dispatch(fetchUser(...args))),
  updateSuitabilityInfo: (...args) => (dispatch(updateSuitabilityInfo(...args))),
  changeFieldValue: (model, value) => {
    dispatch(actions.change(`suitabilityForm.suitabilityInformation${model}`, value));
  },
  changeValidity: (model, value) => {
    dispatch(actions.validateErrors(`suitabilityForm.suitabilityInformation${model}`, value));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Suitability);



// WEBPACK FOOTER //
// ./src/components/Suitability/index.js